import React, { useState, useRef } from 'react';
import styled from '@emotion/styled';
import { colors, typography } from '../theme/tokens';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import { useNavigate } from 'react-router-dom';
import { TitleLarge, SubtitleMedium } from '../components/Typography';
import { Upload, Check, X } from '@phosphor-icons/react';
import { analyzeRecipe, compressImage, RecipeAnalysisRequest, RecipeAnalysisResponse } from '../utils/ai';
import { useToast } from '../contexts/ToastContext';
import { supabase } from '../config/supabase';

const PageContainer = styled.div`
  padding: 1rem;
  width: 100%;
  box-sizing: border-box;
`;

const ContentContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid ${colors.neutral.offWhite};
  background-color: ${colors.neutral.white};
  border-top-left-radius: 8px;
  border-top-right-radius: 8px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const HeaderButtons = styled.div`
  display: flex;
  gap: 1rem;
`;

const HeaderTitle = styled(TitleLarge)`
  color: ${colors.brand.laurel};
  margin-bottom: 0.5rem;
`;

const FormContainer = styled.div`
  padding: 1rem;
  background-color: ${colors.neutral.white};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  width: 100%;
  box-sizing: border-box;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: ${colors.neutral.charcoal};
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 200px;
  padding: 1rem;
  border: 1px solid ${colors.neutral.lightCharcoal};
  border-radius: 8px;
  font-family: inherit;
  font-size: 1rem;
  resize: vertical;
  background-color: ${colors.neutral.white};
  color: ${colors.neutral.black};
  
  &:focus {
    outline: none;
    border-color: ${colors.brand.laurel};
  }
`;

const UploadButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  align-self: flex-start;
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: ${colors.neutral.offWhite};
  border-radius: 4px;
  font-size: 0.875rem;
  color: ${colors.neutral.charcoal};
`;

const LoadingSpinner = styled.div`
  width: 20px;
  height: 20px;
  border: 2px solid ${colors.neutral.lightCharcoal};
  border-top-color: ${colors.brand.laurel};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.reds.bright};
  font-size: 0.875rem;
  margin-top: 0.5rem;
`;

const SmallButton = styled(Button)`
  padding: 0.25rem 0.5rem;
  font-size: 0.75rem;
`;

const PreviewContainer = styled.div`
`;

const PreviewTitle = styled.h3`
  color: ${colors.brand.laurel};
  font-family: ${typography.label.large.fontFamily};
  font-size: ${typography.label.large.fontSize};
  margin-bottom: 1rem;
`;

const PreviewSection = styled.div`
  margin-bottom: 1rem;
`;

const PreviewLabel = styled.div`
  font-weight: 600;
  color: ${colors.neutral.darkCharcoal};
  margin-bottom: 0.5rem;
`;

const PreviewValue = styled.div`
  color: ${colors.neutral.charcoal};
  margin-bottom: 0.5rem;
`;

const PreviewIngredients = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const PreviewIngredient = styled.div`
  color: ${colors.neutral.charcoal};
`;

const PreviewSteps = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const PreviewStep = styled.div`
  color: ${colors.neutral.charcoal};
  padding-left: 1rem;
`;

const PreviewActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;

const MissingFieldsWarning = styled.div`
  background: ${colors.reds.bright}15;
  border: 1px solid ${colors.reds.bright}40;
  border-radius: 4px;
  padding: 0.75rem;
  margin-top: 1rem;
  color: ${colors.reds.bright};
  font-size: 0.875rem;
`;

const CreateAI: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [text, setText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [analyzedRecipe, setAnalyzedRecipe] = useState<RecipeAnalysisResponse | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/heic', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPG, PNG, HEIC, or WebP)');
      return;
    }

    // Validate file size (20MB limit)
    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB');
      return;
    }

    setSelectedFile(file);
    setError(null);
  };

  const saveRecipeToDatabase = async (recipeData: RecipeAnalysisResponse, imageUrl?: string) => {
    if (!user?.id) {
      throw new Error('User not authenticated');
    }

    // Get the creator's ID from CreatorDetails using the auth user's ID
    const { data: creatorDetails, error: creatorError } = await supabase
      .from('CreatorDetails')
      .select('id')
      .eq('AuthID', user.id)
      .single();

    if (creatorError) {
      throw new Error('Failed to verify creator account');
    }

    if (!creatorDetails) {
      throw new Error('No creator account found for the current user');
    }

    const creatorId = creatorDetails.id;

    // Format the date as an ISO string
    const now = new Date().toISOString();

    const recipeInsertData = {
      Name: recipeData.name,
      Description: recipeData.description,
      PrepTime: recipeData.prepTime || 0,
      CookTime: recipeData.cookTime || 0,
      Free: false, // Default to false for AI-generated recipes
      ImageURL: imageUrl || '',
      UserID: creatorId,
      DateCreated: now,
      PortionSize: recipeData.portionSize || 1,
      Category: recipeData.categories?.join(', ') || '',
      Publish: false
    };

    // Insert the recipe
    const { data: recipeResult, error: recipeError } = await supabase
      .from('Recipes')
      .insert([recipeInsertData])
      .select()
      .single();

    if (recipeError) throw recipeError;
    if (!recipeResult) throw new Error('Failed to create recipe - no ID returned');

    // Save ingredients if they exist
    if (recipeData.ingredients && recipeData.ingredients.length > 0) {
      const ingredientPromises = recipeData.ingredients.map(async (ingredient) => {
        // First, try to find existing ingredient or create new one
        let ingredientId: number;

        const { data: existingIngredient } = await supabase
          .from('Ingredients')
          .select('id')
          .eq('Name', ingredient.name)
          .single();

        if (existingIngredient) {
          ingredientId = existingIngredient.id;
        } else {
          // Create new ingredient
          const { data: newIngredient, error: newIngredientError } = await supabase
            .from('Ingredients')
            .insert([{ Name: ingredient.name, Category: 'Other' }])
            .select('id')
            .single();

          if (newIngredientError) throw newIngredientError;
          ingredientId = newIngredient.id;
        }

        // Create recipe ingredient relationship
        return {
          RecipeID: recipeResult.RecipeId,
          IngredientID: ingredientId,
          Quantity: ingredient.amount || 0,
          Unit: ingredient.unit || 'piece'
        };
      });

      const recipeIngredients = await Promise.all(ingredientPromises);

      if (recipeIngredients.length > 0) {
        const { error: ingredientsError } = await supabase
          .from('RecipeIngredients')
          .insert(recipeIngredients);

        if (ingredientsError) throw ingredientsError;
      }
    }

    // Save steps if they exist
    if (recipeData.steps && recipeData.steps.length > 0) {
      const stepsData = recipeData.steps.map((step, index) => ({
        RecipeID: recipeResult.RecipeId,
        Step: index + 1,
        Instructions: step.instructions
      }));

      const { error: stepsError } = await supabase
        .from('RecipeSteps')
        .insert(stepsData);

      if (stepsError) throw stepsError;
    }

    return recipeResult.RecipeId;
  };

  const handleAnalyze = async () => {
    if (!text.trim() && !selectedFile) {
      setError('Please enter text or upload an image');
      return;
    }

    setIsAnalyzing(true);
    setError(null);
    setAnalyzedRecipe(null);
    setShowPreview(false);

    try {
      let request: RecipeAnalysisRequest = {};

      if (text.trim()) {
        request.text = text.trim();
      }

      if (selectedFile) {
        // Compress image if needed and convert to base64
        const compressedImage = await compressImage(selectedFile, 5);
        const base64Data = compressedImage.split(',')[1]; // Remove data URL prefix
        request.imageBase64 = base64Data;
        request.imageType = selectedFile.type;
      }

      const result = await analyzeRecipe(request);

      if (!result.success) {
        throw new Error(result.error || 'Failed to analyze recipe');
      }

      const recipeData = result.recipe;
      
      if (!recipeData) {
        throw new Error('No recipe data received from analysis');
      }

      setAnalyzedRecipe(recipeData);
      setShowPreview(true);

      showToast('Recipe analyzed successfully! Review the details below.', 'success');

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to analyze recipe';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSaveRecipe = async () => {
    if (!analyzedRecipe) return;

    setIsAnalyzing(true);
    setError(null);

    try {
      // Save recipe to database
      let imageUrl = '';
      if (selectedFile) {
        // TODO: Upload image to Cloudinary and get URL
        // For now, we'll save without image and let user upload later
        showToast('Please upload the recipe image in the next step', 'info');
      }

      const recipeId = await saveRecipeToDatabase(analyzedRecipe, imageUrl);

      // Redirect to the recipe creation page for editing
      navigate(`/recipes/create/${recipeId}`);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to save recipe';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleTryAgain = () => {
    setAnalyzedRecipe(null);
    setShowPreview(false);
    setError(null);
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const removeFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <PageContainer>
      <ContentContainer>
        <Header>
          <HeaderContent>
            <HeaderTitle>AI Recipe Assistant</HeaderTitle>
            <SubtitleMedium>Generate recipes with AI assistance</SubtitleMedium>
          </HeaderContent>
          <HeaderButtons>
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/recipes')}
            >
              Cancel
            </Button>
          </HeaderButtons>
        </Header>
        
        <FormContainer>
          {!showPreview ? (
            <Form onSubmit={(e) => { e.preventDefault(); handleAnalyze(); }}>
              <FormGroup>
                <Label>Recipe Description</Label>
                <TextArea 
                  placeholder="Enter or paste your recipe description here..."
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  disabled={isAnalyzing}
                />
              </FormGroup>
              
              <FormGroup>
                <Label>Upload Recipe Image (Optional)</Label>
                <UploadButton 
                  type="button"
                  onClick={handleUploadClick}
                  disabled={isAnalyzing}
                >
                  <Upload size={20} />
                  Upload Image
                </UploadButton>
                <HiddenFileInput
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/heic,image/webp"
                  onChange={handleFileSelect}
                />
                
                {selectedFile && (
                  <FileInfo>
                    <span>📎 {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)}MB)</span>
                    <SmallButton 
                      type="button" 
                      variant="outline" 
                      onClick={removeFile}
                      disabled={isAnalyzing}
                    >
                      Remove
                    </SmallButton>
                  </FileInfo>
                )}
              </FormGroup>

              {error && <ErrorMessage>{error}</ErrorMessage>}

              <Button 
                type="submit" 
                disabled={isAnalyzing || (!text.trim() && !selectedFile)}
              >
                {isAnalyzing ? (
                  <>
                    <LoadingSpinner />
                    Analyzing Recipe...
                  </>
                ) : (
                  'Analyze Recipe'
                )}
              </Button>
            </Form>
          ) : (
            <div>
              <PreviewContainer>
                <PreviewTitle>Recipe Analysis Results</PreviewTitle>
                
                <PreviewSection>
                  <PreviewLabel>Recipe Name</PreviewLabel>
                  <PreviewValue>{analyzedRecipe?.name || 'Not specified'}</PreviewValue>
                </PreviewSection>

                <PreviewSection>
                  <PreviewLabel>Description</PreviewLabel>
                  <PreviewValue>{analyzedRecipe?.description || 'Not specified'}</PreviewValue>
                </PreviewSection>

                <PreviewSection>
                  <PreviewLabel>Timing</PreviewLabel>
                  <PreviewValue>
                    Prep: {analyzedRecipe?.prepTime || 0} min | 
                    Cook: {analyzedRecipe?.cookTime || 0} min | 
                    Serves: {analyzedRecipe?.portionSize || 1}
                  </PreviewValue>
                </PreviewSection>

                {analyzedRecipe?.categories && analyzedRecipe.categories.length > 0 && (
                  <PreviewSection>
                    <PreviewLabel>Categories</PreviewLabel>
                    <PreviewValue>{analyzedRecipe.categories.join(', ')}</PreviewValue>
                  </PreviewSection>
                )}

                {analyzedRecipe?.ingredients && analyzedRecipe.ingredients.length > 0 && (
                  <PreviewSection>
                    <PreviewLabel>Ingredients</PreviewLabel>
                    <PreviewIngredients>
                      {analyzedRecipe.ingredients.map((ingredient, index) => (
                        <PreviewIngredient key={index}>
                          • {ingredient.amount} {ingredient.unit} {ingredient.name}
                        </PreviewIngredient>
                      ))}
                    </PreviewIngredients>
                  </PreviewSection>
                )}

                {analyzedRecipe?.steps && analyzedRecipe.steps.length > 0 && (
                  <PreviewSection>
                    <PreviewLabel>Instructions</PreviewLabel>
                    <PreviewSteps>
                      {analyzedRecipe.steps.map((step, index) => (
                        <PreviewStep key={index}>
                          {step.step}. {step.instructions}
                        </PreviewStep>
                      ))}
                    </PreviewSteps>
                  </PreviewSection>
                )}

                {analyzedRecipe?.missingFields && analyzedRecipe.missingFields.length > 0 && (
                  <MissingFieldsWarning>
                    ⚠️ Missing information: {analyzedRecipe.missingFields.join(', ')}
                  </MissingFieldsWarning>
                )}

                <PreviewActions>
                  <Button 
                    onClick={handleSaveRecipe}
                    disabled={isAnalyzing}
                  >
                    {isAnalyzing ? (
                      <>
                        <LoadingSpinner />
                        Saving Recipe...
                      </>
                    ) : (
                      <>
                        <Check size={20} />
                        Save Recipe
                      </>
                    )}
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={handleTryAgain}
                    disabled={isAnalyzing}
                  >
                    <X size={20} />
                    Try Again
                  </Button>
                </PreviewActions>
              </PreviewContainer>
            </div>
          )}
        </FormContainer>
      </ContentContainer>
    </PageContainer>
  );
};

export default CreateAI; 